import React, { useEffect, useState } from "react";
import { CreditCard, CalendarClock, Package, Receipt } from "lucide-react";
import PageHeader from "../../components/ui/PageHeader";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { BetaBadge } from "../../components/ui/BetaBadge";
import { useToast } from "../../hooks/useToast";
import {
  BillingHistoryItem,
  BillingOverview,
  formatBillingAmount,
  formatBillingCycle,
  formatCardBrand,
  formatSubscriptionStatus,
  getBillingHistory,
  getBillingOverview,
} from "../../services/billing";

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function statusBadgeClass(status?: string | null): string {
  const raw = String(status || "").toLowerCase();
  if (raw === "active") return "bg-green-50 text-green-700 border-green-100";
  if (raw === "past_due") return "bg-amber-50 text-amber-700 border-amber-100";
  if (raw === "pending_setup" || raw === "pending") {
    return "bg-blue-50 text-blue-700 border-blue-100";
  }
  return "bg-gray-50 text-gray-600 border-gray-100";
}

const BillingSettings: React.FC = () => {
  const toast = useToast();
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [history, setHistory] = useState<BillingHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  useEffect(() => {
    let cancelled = false;

    async function loadOverview() {
      setLoadingOverview(true);
      try {
        const overviewData = await getBillingOverview();
        if (!cancelled) {
          setOverview(overviewData);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Failed to load billing.";
          toast.error(message);
        }
      } finally {
        if (!cancelled) {
          setLoadingOverview(false);
        }
      }
    }

    loadOverview();

    return () => {
      cancelled = true;
    };
    // useToast() returns a new object each render — do not add toast to deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadHistory() {
      setLoadingHistory(true);
      try {
        const historyData = await getBillingHistory(page);
        if (!cancelled) {
          setHistory(historyData.items);
          setLastPage(historyData.pagination.last_page);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          const message =
            error instanceof Error ? error.message : "Failed to load billing history.";
          toast.error(message);
        }
      } finally {
        if (!cancelled) {
          setLoadingHistory(false);
        }
      }
    }

    loadHistory();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  if (loadingOverview && !overview) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading billing..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  const subscription = overview?.subscription;
  const planName =
    subscription?.package_name || overview?.plan?.name || "No plan assigned";
  const billingCycle = formatBillingCycle(
    overview?.billing_cycle || subscription?.interval,
  );
  const amountCents =
    subscription?.amount_cents ??
    (billingCycle === "Yearly"
      ? Math.round((overview?.plan?.annual_price || 0) * 100)
      : Math.round((overview?.plan?.monthly_price || 0) * 100));
  const currency = subscription?.currency || "gbp";
  const paymentMethod = overview?.payment_method;

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            Billing
            <BetaBadge />
          </span>
        }
        description="Your subscription plan, payment method, and billing history."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#E5E0EB] bg-white p-5 sm:p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-[#885ABB]/10 p-3 text-[#885ABB]">
              <Package className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Subscription plan
              </p>
              <h2 className="mt-1 text-xl font-bold text-[#402B69]">{planName}</h2>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(
                    overview?.subscription_status || subscription?.status,
                  )}`}
                >
                  {formatSubscriptionStatus(
                    overview?.subscription_status || subscription?.status,
                  )}
                </span>
                <span className="text-sm text-gray-500">{billingCycle}</span>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-[#FAF8FD] border border-[#E5E0EB] p-4">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">
                Amount
              </p>
              <p className="mt-1 text-lg font-bold text-[#885ABB]">
                {formatBillingAmount(amountCents, currency)}
                <span className="text-sm font-medium text-gray-500">
                  {billingCycle === "Yearly" ? " / year" : " / month"}
                </span>
              </p>
            </div>
            <div className="rounded-xl bg-[#FAF8FD] border border-[#E5E0EB] p-4">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">
                Next billing date
              </p>
              <p className="mt-1 text-lg font-bold text-[#402B69]">
                {formatDate(subscription?.next_charge_at)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E5E0EB] bg-white p-5 sm:p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-[#885ABB]/10 p-3 text-[#885ABB]">
              <CreditCard className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Payment method
              </p>
              {paymentMethod?.last4 ? (
                <>
                  <h2 className="mt-1 text-xl font-bold text-[#402B69]">
                    {formatCardBrand(paymentMethod.brand)} •••• {paymentMethod.last4}
                  </h2>
                  <p className="mt-2 text-sm text-gray-500">
                    {paymentMethod.exp_month && paymentMethod.exp_year
                      ? `Expires ${String(paymentMethod.exp_month).padStart(2, "0")}/${paymentMethod.exp_year}`
                      : "Active card on file"}
                  </p>
                </>
              ) : (
                <p className="mt-2 text-sm text-gray-500">No active payment method on file.</p>
              )}
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-dashed border-[#E5E0EB] bg-[#FAF8FD] p-4 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <CalendarClock className="mt-0.5 h-4 w-4 text-[#885ABB]" />
              <span>
                Your subscription renews automatically each billing period using the saved
                card above.
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E5E0EB] bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
          <Receipt className="h-5 w-5 text-[#885ABB]" />
          <div>
            <h3 className="text-lg font-bold text-[#402B69]">Billing history</h3>
            <p className="text-sm text-gray-500">Monthly subscription charges and payments.</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FAF8FD] text-left text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="px-5 py-3 font-bold">Date</th>
                <th className="px-5 py-3 font-bold">Description</th>
                <th className="px-5 py-3 font-bold">Amount</th>
                <th className="px-5 py-3 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-gray-500">
                    No billing history yet.
                  </td>
                </tr>
              ) : (
                history.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100">
                    <td className="px-5 py-4 text-gray-700">{formatDate(row.happened_at)}</td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-800">{row.label}</div>
                      {row.error && (
                        <div className="text-xs text-red-500 mt-1">{row.error}</div>
                      )}
                    </td>
                    <td className="px-5 py-4 font-semibold text-[#402B69]">
                      {formatBillingAmount(row.amount_cents, row.currency)}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(
                          row.event === "recurring_charge_failed" ||
                          row.event === "onboarding_payment_failed"
                            ? "past_due"
                            : row.status,
                        )}`}
                      >
                        {row.event === "recurring_charge_failed" ||
                        row.event === "onboarding_payment_failed"
                          ? "Failed"
                          : formatSubscriptionStatus(row.status || "active")}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {lastPage > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
            <button
              type="button"
              disabled={page <= 1 || loadingHistory}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {page} of {lastPage}
            </span>
            <button
              type="button"
              disabled={page >= lastPage || loadingHistory}
              onClick={() => setPage((current) => Math.min(lastPage, current + 1))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingSettings;
